/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy & Warren Dowey
 */

import { Injectable } from '@angular/core';
import { ProjectModel } from '@app/models/project.model';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { HttpClient } from '@angular/common/http';
import { ProjectsListService } from '@app/services/api-services/projects/projects-list.service';
import { ApiParamsModel } from '@app/models/api-params.model';
import { ApiFilterModel } from '@app/models/api-filter.model';
import { delay } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
import { ApiSortModel } from '@app/models/api-sort.model';
@Injectable({
  providedIn: 'root',
})
export class ProjectService extends ApiBaseService {
  private _project: ProjectModel;
  protected _endpoint = 'projects';

  constructor(
    http: HttpClient,
    authUserService: AuthUserService,
    private projectListService: ProjectsListService
  ) {
    super(http, authUserService);
  }

  get project(): ProjectModel {
    return this._project;
  }

  set project(value: ProjectModel) {
    this._project = value;
  }

  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getProjectFromListById
   *
   * : Get a ProjectModel based on the id of the project-analytics within the model by checking the list
   *
   * @param id of type string
   *
   * @returns ProjectModel: gets the project-analytics with the id that meets the query
   */
  getProjectFromListById(projectId: string): void {
    this.project = this.projectListService.getProjectsById(projectId);
    if (this.project) {
      this.dataSubject.next([this.project]);
    } else {
      this.getProjectById(projectId);
    }
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getProjectById
   *
   * : Get an project-analytics based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: gets the project-analytics with the id that meets the query
   */
  public getProjectById(projectId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: 'project_id',
      operator: 'eq',
      value: projectId,
    };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }

  /**
   *  Warren Dowey
   *
   *  getProjectFromListBySlug
   *
   * : Get a ProjectModel based on the id slug the project-analytics within the model by checking the list
   *
   * @param slug of type string
   *
   * @returns ProjectModel: gets the project-analytics with the slug that meets the query
   */
  getProjectFromListBySlug(slug: string): void {
    this.project = this.projectListService.getProjectBySlug(slug);
    if (this.project) {
      this.dataSubject.next([this.project]);
    } else {
      this.getProjectBySlug(slug);
    }
  }
  /**
   *  Warren Dowey
   *
   *  getProjectBySlug
   *
   * : Get an project-analytics based on the slug by filtering the API query
   *
   * @param slug of type string
   *
   * @returns Void: gets the project-analytics with the slug that meets the query
   */
  public getProjectBySlug(slug: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = { field: 'project_slug', operator: 'eq', value: slug };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }

  /**
   *  Ryan Duffy
   *
   * : Get an project-analytics based on the organisationId and the projectName by filtering the API query
   *
   * @param {string} projectName
   * @param {string} organisationId
   * @memberof ProjectService
   */
  public getFilteredOrganisationProjectByProjectName(
    projectName: string,
    organisationId: string
  ): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: ['project_name', 'organisation_id'],
      operator: ['fts', 'eq'],
      value: [projectName, organisationId],
      joinOperator: 'AND',
    };
    const apiSortModel = new ApiSortModel();
    apiSortModel.direction = 'asc';
    apiSortModel.field = 'project_name';
    apiParamsModel.filter = [apiFilterModel];
    apiParamsModel.sort = [apiSortModel];
    this.getData(apiParamsModel);
  }

  /**
   *  Ryan Duffy
   *
   *  getOrganisationProjects
   *
   * : Get an project-analytics based on the organisationId by filtering the API query, limited
   *
   * @param {string} organisationId
   * @memberof ProjectService
   */
  public getOrganisationProjects(organisationId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: 'organisation_id',
      operator: 'eq',
      value: organisationId,
    };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }

  /**
   *  Ryan Duffy
   *
   *  getOrganisationProjectByIsClosedAndLimit
   *
   * : Get an project-analytics based on the closed status and the organisationId by filtering the API query, limited
   *
   * @param {boolean} closed
   * @param {string} organisationId
   * @param {number} limit
   * @memberof ProjectService
   */
  public getOrganisationProjectByIsClosedAndLimit(
    closed: boolean,
    organisationId: string,
    limit: number
  ): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: ['project_closed', 'organisation_id'],
      operator: ['eq', 'eq'],
      value: [closed, organisationId],
      joinOperator: 'AND',
    };
    const apiSortModel = new ApiSortModel();
    apiSortModel.direction = 'asc';
    apiSortModel.field = 'project_name';
    apiParamsModel.filter = [apiFilterModel];
    apiParamsModel.sort = [apiSortModel];
    apiParamsModel.limit = [limit];
    this.getData(apiParamsModel);
  }

  /**
   *  getOrganisationProjectsFromList
   *
   * @param {string} organisationId
   * @memberof ProjectService
   */
  getOrganisationProjectsFromList(organisationId: string) {
    const projectsList = this.projectListService.getProjectsByOrganisationId(organisationId);
    let currentFetchTimestamp: number;
    let lastListFetch: number;
    let timeDifferenceFetches: number;
    if (projectsList.length) {
      currentFetchTimestamp = new Date().getTime();
      lastListFetch = this.projectListService.fetchTimestamp
        ? this.projectListService.fetchTimestamp.getTime()
        : null;
      // Get the difference between the current time and the last time the list was fully fetched
      // This can be compared to see if we need to pull from the API and list rather than just one.
      timeDifferenceFetches = lastListFetch ? (currentFetchTimestamp - lastListFetch) / 1000 : null;
      // This subscription is used to ensure that the dataSubject is returned after the first event loop
      // This allows the resolvers to be completed rather than bugs occurring without the delay being implemented
      this.delaySubscription = of(projectsList)
        .pipe(delay(0))
        .subscribe((projects) => {
          // If the time difference betweem the current time and the last fetch is more than ten seconds
          // Pass the local list, also pass the notes from the API, also get the newest full list from the API
          if (timeDifferenceFetches && timeDifferenceFetches > 10) {
            this.dataSubject.next(projects);
            this.getOrganisationProjects(organisationId);
            this.projectListService.getData({});
          } else {
            this.dataSubject.next(projects);
          }
          if (this.delaySubscription) {
            this.delaySubscription.unsubscribe();
          }
        });
      // If there is no local list, get the correct list from the API and also get the newest full list from the API
    } else {
      this.getOrganisationProjects(organisationId);
      this.projectListService.getData({});
    }
  }
  /**
   *   Ryan Duffy
   *
   *  getProjectFromListByProjectSlug
   *
   * : Get Projects based on the slug by filtering the API query
   *
   * @param slug of type string
   *
   * @returns Voslug: gets Projects with the slug that meets the query
   */
  public getProjectFromListByProjectSlug(projectSlug: string): void {
    const projectsList = this.projectListService.getProjectsBySlug(projectSlug);
    let currentFetchTimestamp: number;
    let lastListFetch: number;
    let timeDifferenceFetches: number;
    if (projectsList.length) {
      currentFetchTimestamp = new Date().getTime();
      lastListFetch = this.projectListService.fetchTimestamp
        ? this.projectListService.fetchTimestamp.getTime()
        : null;
      // Get the difference between the current time and the last time the list was fully fetched
      // This can be compared to see if we need to pull from the API and list rather than just one.
      timeDifferenceFetches = lastListFetch ? (currentFetchTimestamp - lastListFetch) / 1000 : null;
      // This subscription is used to ensure that the dataSubject is returned after the first event loop
      // This allows the resolvers to be completed rather than bugs occurring without the delay being implemented
      this.delaySubscription = of(projectsList)
        .pipe(delay(0))
        .subscribe((projects) => {
          // If the time difference betweem the current time and the last fetch is more than ten seconds
          // Pass the local list, also pass the notes from the API, also get the newest full list from the API
          if (timeDifferenceFetches && timeDifferenceFetches > 10) {
            this.dataSubject.next(projects);
            this.getProjectBySlug(projectSlug);
            this.projectListService.getData({});
          } else {
            this.dataSubject.next(projects);
          }
          if (this.delaySubscription) {
            this.delaySubscription.unsubscribe();
          }
        });
      // If there is no local list, get the correct list from the API and also get the newest full list from the API
    } else {
      this.getProjectBySlug(projectSlug);
      this.projectListService.getData({});
    }
  }

  /**
   *  Ryan Duffy
   *
   *  getFilteredOrganisationProjectsFromListByProjectName
   *
   * : Get Projects based on the name and organisationId, using full text search,
   * as well as sorting the data as requireds

   *
   * @param {string} projectName
   * @param {string} organisationId
   * @memberof ProjectService
   */
  public getFilteredOrganisationProjectsFromListByProjectName(
    projectName: string,
    organisationId: string
  ): void {
    const projectsList = this.projectListService
      .getFilteredOrganisationProjectsByProjectName(projectName, organisationId)
      .sort((project1, project2) => {
        if (project1.projectName > project2.projectName) {
          return 1;
        }
        if (project1.projectName < project2.projectName) {
          return -1;
        }
        return 0;
      });
    if (projectsList.length) {
      // This subscription is used to ensure that the dataSubject is returned after the first event loop
      // This allows the resolvers to be completed rather than bugs occurring without the delay being implemented
      this.delaySubscription = of(projectsList)
        .pipe(delay(0))
        .subscribe((projects) => {
          // If the time difference betweem the current time and the last fetch is more than ten seconds
          // Pass the local list, also pass the notes from the API, also get the newest full list from the API
          this.dataSubject.next(projects);
          if (this.delaySubscription) {
            this.delaySubscription.unsubscribe();
          }
        });
      // If there is no local list, get the correct list from the API and also get the newest full list from the API
    } else {
      this.getFilteredOrganisationProjectByProjectName(projectName, organisationId);
      this.projectListService.getData({});
    }
  }

  /**
   *   Ryan Duffy
   *
   *  getPOrganisationProjectsFromListByIsClosed
   *
   * : Get required projects-list
   *
   * @returns Void: gets all projects-list from the list or the API
   */
  public getPOrganisationProjectsFromListByIsClosedAndLimit(
    closed: boolean,
    organisationId: string,
    limit: number
  ): void {
    const projectList = this.projectListService
      .getOrganisationProjectsByIsClosed(closed, organisationId)
      .sort((project1, project2) => {
        if (project1.projectName.toLowerCase() > project2.projectName.toLowerCase()) {
          return 1;
        }
        if (project1.projectName.toLowerCase() < project2.projectName.toLowerCase()) {
          return -1;
        }
        return 0;
      })
      .slice(0, limit);
    let currentFetchTimestamp: number;
    let lastListFetch: number;
    let timeDifferenceFetches: number;
    if (projectList.length) {
      currentFetchTimestamp = new Date().getTime();
      lastListFetch = this.projectListService.fetchTimestamp
        ? this.projectListService.fetchTimestamp.getTime()
        : null;
      // Get the difference between the current time and the last time the list was fully fetched
      // This can be compared to see if we need to pull from the API and list rather than just one.
      timeDifferenceFetches = lastListFetch ? (currentFetchTimestamp - lastListFetch) / 1000 : null;
      // This subscription is used to ensure that the dataSubject is returned after the first event loop
      // This allows the resolvers to be completed rather than bugs occurring without the delay being implemented
      this.delaySubscription = of(projectList)
        .pipe(delay(0))
        .subscribe((projects) => {
          // If the time difference betweem the current time and the last fetch is more than ten seconds
          // Pass the local list, also pass the notes from the API, also get the newest full list from the API
          if (timeDifferenceFetches && timeDifferenceFetches > 60) {
            this.dataSubject.next(projects);
            this.getOrganisationProjectByIsClosedAndLimit(closed, organisationId, limit);
            this.projectListService.getData({});
          } else {
            this.dataSubject.next(projects);
          }
          if (this.delaySubscription) {
            this.delaySubscription.unsubscribe();
          }
        });
      // If there is no local list, get the correct list from the API and also get the newest full list from the API
    } else {
      this.getOrganisationProjectByIsClosedAndLimit(closed, organisationId, limit);
      this.projectListService.getData({});
    }
  }
  /**
   *  update a specific project-analytics by ID
   *
   * @param {ProjectModel} data
   * @memberof ProjectService
   */
  updateProjectByProjectId(data: ProjectModel) {
    const apiFilter = new ApiFilterModel();
    const apiParams = new ApiParamsModel();
    let projectConverted: Object;
    apiFilter.field = 'project_id';
    apiFilter.operator = 'eq';
    apiFilter.value = data.projectId;
    apiParams.filter = [apiFilter];
    projectConverted = data.toJson();
    if (!projectConverted['project_data_source_url2']) {
      projectConverted['project_data_source_url2'] = null;
    }
    this.updateData(apiParams, projectConverted);
    this.projectListService.updateInList(data);
  }

  filterData(apiParams) {
    this.getData(apiParams);
  }

  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleData
   *
   * : handles the data from the list by extending the handleData function from the API base service
   *
   * @param data of type ProjectModel
   *
   * @returns Void: handle a get request from the postgres DB
   */
  protected handleData(data: ProjectModel[]): void {
    this.project = data[0];
    super.handleData(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleInsert
   *
   * : handles the insert of a new project-analytics into the list
   *
   * @param data of type ProjectModel
   *
   * @returns Void: inserts a new project-analytics into the lsit
   */
  protected handleInsert(data: ProjectModel[]): void {
    if (data) {
      this.project = data[0];
      this.projectListService.addToList(data[0]);
    }
    super.handleInsert(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleUpdate
   *
   * : handles the update of an project-analytics in the list
   *
   * @param data of type ProjectModel
   *
   * @returns Void: update the lsit
   */
  protected handleUpdate(data: ProjectModel[]): void {
    this.project = data[0];
    this.projectListService.updateInList(data[0]);
    super.handleUpdate(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleDelete
   *
   * : deletes an project-analytics from the list
   *
   * @param data of type projectModel
   *
   * @returns Void: deletes a project-analytics from the list
   */
  protected handleDelete(data: ProjectModel[]): void {
    if (data) {
      this.projectListService.removeFromList(data[0]);
    }
    super.handleDelete(data);
  }
}
